// src/features/payment/paymentSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import paymentService from './paymentService'

// Async thunk for payment initialization
export const initializePayment = createAsyncThunk(
  'payment/initialize',
  async (paymentData, { rejectWithValue }) => {
    try {
      return await paymentService.initializePayment(paymentData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check payment status
export const checkPaymentStatus = createAsyncThunk(
  'payment/checkStatus',
  async (conversationId, { rejectWithValue }) => {
    try {
      return await paymentService.checkPaymentStatus(conversationId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  isLoading: false,
  paymentUrl: null,
  selectedPlan: null,
  showModal: false,
  paymentStatus: null,
  error: null,
  conversationId: null,
  paymentHistory: []
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    // Synchronous actions
    setShowModal: (state, action) => {
      state.showModal = action.payload;
    },
    clearPaymentData: (state) => {
      state.paymentUrl = null;
      state.selectedPlan = null;
      state.paymentStatus = null;
      state.error = null;
      state.conversationId = null;
    },
    setSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    addToPaymentHistory: (state, action) => {
      state.paymentHistory.push({
        ...action.payload,
        timestamp: new Date().toISOString()
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Initialize Payment
      .addCase(initializePayment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializePayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentUrl = action.payload.paymentPageUrl;
        state.conversationId = action.payload.conversationId;
        state.showModal = true;
      })
      .addCase(initializePayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Check Payment Status
      .addCase(checkPaymentStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkPaymentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentStatus = action.payload.status;
        if (action.payload.status === 'completed') {
          state.showModal = false;
        }
      })
      .addCase(checkPaymentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  setShowModal, 
  clearPaymentData, 
  setSelectedPlan, 
  addToPaymentHistory 
} = paymentSlice.actions;

export default paymentSlice.reducer;

// Selectors
export const selectPayment = (state) => state.payment;
export const selectIsPaymentLoading = (state) => state.payment.isLoading;
export const selectPaymentUrl = (state) => state.payment.paymentUrl;
export const selectShowModal = (state) => state.payment.showModal;
export const selectSelectedPlan = (state) => state.payment.selectedPlan;
export const selectPaymentHistory = (state) => state.payment.paymentHistory;
